import { debounce } from "@mui/material";

import { SEARCH_FIELD_LIMIT } from "@sinch/core/config/globals";
import { useLazyQueryHandler } from "@sinch/hooks/useLazyQueryHandler";
import { mapToOptions, Option } from "@sinch/utils/option";
import React, { FC, Ref, useMemo } from "react";
import { AutocompleteInput } from "../../../form/AutocompleteInput/AutocompleteInput";
import { AttendanceChip } from "../../entities/AttendanceChip";
import { clearPrefixForSearch, PrefixedEntities, SinchEntity, useSinchEntity } from "../../entities/entitiesProperties";
import { ShiftsData } from "./queries.graphql";
import { ShiftsDataQuery, ShiftsDataQueryVariables } from "./queries.types";
import { AutocompleteInputProps, AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";

interface ShiftsSelectInputProps
  extends Pick<AutocompleteInputProps<AutocompleteOptions, true>, "slotProps" | "textFieldProps"> {
  /**
   * Input name
   */
  name: string;
  label: string;
  required?: boolean;
  value: (string | number)[] | undefined;
  onChange?: (val: (string | number)[] | null) => void;
  disabled?: boolean;
  error?: boolean;
  errorMessage: string[];
  inputRef: Ref<any>;
  onBlur: () => void;
  /**
   * Limit searched list of options
   */
  limit?: number;
}

export const ShiftsSelectInput: FC<ShiftsSelectInputProps> = ({ limit = SEARCH_FIELD_LIMIT, ...field }) => {
  const { getEntityIdLabel } = useSinchEntity();

  const { data, dispatch, loading } = useLazyQueryHandler<ShiftsDataQuery, ShiftsDataQueryVariables>(ShiftsData);
  const options: Option[] = useMemo(
    () =>
      mapToOptions("id", ({ name, id }) => `${getEntityIdLabel(SinchEntity.Shift, id)} ${name}`)(data?.shifts || []),
    [data?.shifts]
  );

  const debounceDispatch = useMemo(() => debounce(dispatch, 200), [dispatch]);

  const handleSearch = (search: string) => {
    debounceDispatch({ limit, filter: { search: { LIKE: clearPrefixForSearch(search, PrefixedEntities.Shift) } } });
  };

  return (
    <AutocompleteInput<AutocompleteOptions, true>
      size="small"
      {...field}
      limitCount={limit}
      loading={loading}
      multiple
      onSearch={handleSearch}
      openOnFocus
      options={options}
      renderTags={(vals, getTagProps) =>
        vals.map((val, index) => (
          <AttendanceChip
            key={val.value}
            getTagProps={getTagProps}
            index={index}
            val={val}
            variant={SinchEntity.Shift}
          />
        ))
      }
    />
  );
};
